import React, { useEffect, useRef, useState } from 'react';
import { TRANSLATIONS } from '../../../translations';

const key = new TextEncoder().encode(process.env.REACT_APP_LEGAL_KEY);

async function encrypt(text) {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const keyObj = await crypto.subtle.importKey('raw', key, 'AES-CBC', false, [
    'encrypt',
  ]);
  const encodedText = new TextEncoder().encode(text);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    keyObj,
    encodedText
  );
  const ivBase64 = btoa(String.fromCharCode(...iv));
  const encryptedBase64 = btoa(
    String.fromCharCode(...new Uint8Array(encrypted))
  );
  return ivBase64 + ':' + encryptedBase64;
}

async function decrypt(encrypted) {
  try {
    if (typeof encrypted !== 'string' || !encrypted.includes(':')) {
      throw new Error(
        'Invalid encrypted format: Expected "ivBase64:ciphertextBase64"'
      );
    }
    let [ivBase64, encryptedBase64] = encrypted.split(':');
    ivBase64 = ivBase64.replace(/\\\//g, '/');
    encryptedBase64 = encryptedBase64.replace(/\\\//g, '/');

    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    if (!base64Regex.test(ivBase64) || !base64Regex.test(encryptedBase64)) {
      throw new Error('Invalid Base64 encoding in IV or ciphertext');
    }

    let iv, encryptedBytes;
    try {
      iv = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0));
      encryptedBytes = Uint8Array.from(atob(encryptedBase64), (c) =>
        c.charCodeAt(0)
      );
    } catch (e) {
      throw new Error('Base64 decoding failed: ' + e.message);
    }

    if (iv.length !== 16) {
      throw new Error('Invalid IV length: Expected 16 bytes');
    }

    const keyObj = await crypto.subtle.importKey('raw', key, 'AES-CBC', false, [
      'decrypt',
    ]);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      keyObj,
      encryptedBytes
    );
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    // console.error('Decryption error:', error.message);
    throw error;
  }
}

const TextToCanvas = ({
  company,
  vatNumber,
  taxId,
  tourOperator,
  font = '16px "DM Sans",sans-serif',
  textColor = '#181f36',
  backgroundColor = 'transparent',
  padding = 10,
  maxWidth = Infinity,
  lineHeight = 1.2,
  textAlign = 'left',
  className = '',
}) => {
  const [decryptedCompany, setDecryptedCompany] = useState('');
  const [decryptedVatNumber, setDecryptedVatNumber] = useState('');
  const [decryptedTaxId, setDecryptedTaxId] = useState('');
  const [decryptedTourOperator, setDecryptedTourOperator] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    async function handleCrypto() {
      try {
        if (tourOperator) {
          // If tour operator full text is present, decrypt and use it directly
          const dTourOperator = await decrypt(tourOperator);
          setDecryptedTourOperator(dTourOperator);
        } else {
          // Otherwise, decrypt individual fields for partner data
          if (company) {
            const dCompany = await decrypt(company);
            setDecryptedCompany(dCompany);
          }
          if (vatNumber) {
            const dVatNumber = await decrypt(vatNumber);
            setDecryptedVatNumber(dVatNumber);
          }
          if (taxId) {
            const dTaxId = await decrypt(taxId);
            setDecryptedTaxId(dTaxId);
          }
        }
      } catch (error) {
        // console.error('Crypto error:', error);
        setMessage(TRANSLATIONS.LEGAL_INFO_DESCRIPTION);
      }
    }
    handleCrypto();
  }, [company, vatNumber, taxId, tourOperator]);

  useEffect(() => {
    if (decryptedTourOperator) {
      // If we have tour operator text, use it directly
      setMessage(decryptedTourOperator);
    } else if (decryptedCompany) {
      // Otherwise, build the message from partner data only if we have company data
      let taxInfo = '';
      if (
        decryptedVatNumber &&
        (isNaN(Number(decryptedVatNumber)) || Number(decryptedVatNumber) > 0)
      ) {
        taxInfo = `(${TRANSLATIONS.VAT_NUMBER_EXTENDED}: ${decryptedVatNumber})`;
      } else if (decryptedTaxId) {
        taxInfo = `(${TRANSLATIONS.TAX_ID}: ${decryptedTaxId})`;
      }
      setMessage(
        `${TRANSLATIONS.THIS_ACTIVITY_IS_OFFERED_BY} ${decryptedCompany} ${taxInfo}.\n${TRANSLATIONS.LEGAL_INFO_DESCRIPTION}`
      );
    } else {
      // Fallback message
      setMessage(TRANSLATIONS.LEGAL_INFO_DESCRIPTION);
    }
  }, [decryptedCompany, decryptedVatNumber, decryptedTaxId, decryptedTourOperator]);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');

    const dpr = window.devicePixelRatio || 1;

    const containerWidth =
      container.parentElement?.getBoundingClientRect().width || maxWidth;
    const parentWidth = Math.min(containerWidth - 40, maxWidth);

    ctx.font = font;

    const paragraphs = message.split('\n');
    const lines = [];
    const availableWidth = parentWidth - padding * 2;

    paragraphs.forEach((paragraph) => {
      const words = paragraph.split(' ');
      let currentLine = '';

      words.forEach((word, index) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width <= availableWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }

        if (index === words.length - 1 && currentLine) {
          lines.push(currentLine);
        }
      });

      lines.push('');
    });

    const fontSize = parseInt(font.match(/\d+/)?.[0] || 16);
    const calculatedHeight = lines.length * fontSize * lineHeight + padding * 2;

    canvas.width = parentWidth * dpr;
    canvas.height = calculatedHeight * dpr;

    ctx.scale(dpr, dpr);

    canvas.style.width = `${parentWidth}px`;
    canvas.style.height = `${calculatedHeight}px`;

    ctx.clearRect(0, 0, parentWidth, calculatedHeight);

    if (backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, parentWidth, calculatedHeight);
    }

    ctx.font = font;
    ctx.fillStyle = textColor;
    ctx.textAlign = textAlign;
    ctx.textBaseline = 'top';

    let y = padding;
    lines.forEach((line) => {
      const x =
        textAlign === 'center'
          ? parentWidth / 2
          : textAlign === 'right'
            ? parentWidth - padding
            : padding;

      ctx.fillText(line, x, y);
      y += fontSize * lineHeight * (line === '' ? 1.5 : 1); // More space after paragraph
    });
  }, [
    message,
    font,
    textColor,
    backgroundColor,
    padding,
    maxWidth,
    lineHeight,
    textAlign,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        padding: '20px',
        boxSizing: 'border-box',
      }}
    >
      <canvas ref={canvasRef} className={className} />
    </div>
  );
};

export default TextToCanvas;
